import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { motion } from "framer-motion";
import { CreditCard, Plus, Trash2, Check, Loader2, Shield, Lock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_51SWeHe0EstMCMKUETkAyIOgYsayQTILOHFNjfLl8tKarqRA9f9LLi3e4OVp0TQPZVqmLx1Rmpn00g0Q0AHOTBwun00yH18Fyv6");

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#1c1917",
      fontFamily: '"Inter", system-ui, sans-serif',
      "::placeholder": {
        color: "#a8a29e",
      },
      padding: "12px",
    },
    invalid: {
      color: "#dc2626",
      iconColor: "#dc2626",
    },
  },
  hidePostalCode: true,
};

// Add Card Form Component
const AddCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create SetupIntent on backend
      const setupResponse = await axios.post(`${API}/payments/setup-intent`, {
        email: user.email,
        name: user.name
      });

      const { client_secret, setup_intent_id } = setupResponse.data;

      // Step 2: Confirm SetupIntent with card details using Stripe.js
      // Card details are sent directly to Stripe, never to our server
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: user.name,
              email: user.email,
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
        return;
      }

      if (setupIntent.status === "succeeded") {
        // Step 3: Confirm with backend to save card token
        await axios.post(`${API}/payments/confirm-setup`, null, {
          params: {
            setup_intent_id: setup_intent_id,
            email: user.email
          }
        });

        toast.success("Card added successfully!");
        onSuccess();
      }
    } catch (err) {
      const message = getErrorMessage(err, "Failed to add card");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-stone-200 rounded-xl bg-white">
        <CardElement options={cardElementOptions} />
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-stone-500">
        <Lock className="w-3 h-3" />
        Your card details are securely processed by Stripe. We never store your card number.
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="btn-primary flex-1"
          disabled={!stripe || loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Add Card
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Main Payment Methods Page
const PaymentMethodsPage = () => {
  const { user } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  useEffect(() => {
    if (user?.email) {
      fetchPaymentMethods();
    }
  }, [user]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payments/methods/${user.email}`);
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!confirm("Are you sure you want to remove this card?")) return;

    setDeletingId(paymentMethodId);
    try {
      await axios.delete(`${API}/payments/methods/${user.email}/${paymentMethodId}`);
      toast.success("Card removed successfully");
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to remove card");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    setSettingDefaultId(paymentMethodId);
    try {
      await axios.post(`${API}/customers/cards/set-default`, {
        email: user.email,
        payment_method_id: paymentMethodId
      });
      toast.success("Default card updated");
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to set default card");
    } finally {
      setSettingDefaultId(null);
    }
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: "💳",
      mastercard: "💳",
      amex: "💳",
      discover: "💳",
    };
    return icons[brand?.toLowerCase()] || "💳";
  };

  const formatExpiry = (month, year) => {
    return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="payment-methods-page">
      <Navbar />

      {/* Hero */}
      <section className="hero-bg pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="heading-2 text-white mb-4">Payment Methods</h1>
            <p className="body-lg text-stone-300">
              Manage your saved cards for faster checkout
            </p>
          </motion.div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="section-container py-12">
        <div className="max-w-2xl mx-auto">
          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 mb-8 text-emerald-600">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-medium">PCI-DSS Compliant • 256-bit Encryption • Powered by Stripe</span>
          </div>

          {/* Add Card Section */}
          {showAddCard ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 mb-6"
            >
              <h3 className="font-semibold text-emerald-950 mb-4">Add New Card</h3>
              <Elements stripe={stripePromise}>
                <AddCardForm
                  onSuccess={() => {
                    setShowAddCard(false);
                    fetchPaymentMethods();
                  }}
                  onCancel={() => setShowAddCard(false)}
                />
              </Elements>
            </motion.div>
          ) : (
            <Button
              className="w-full mb-6 btn-primary"
              onClick={() => setShowAddCard(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Card
            </Button>
          )}

          {/* Saved Cards List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <h3 className="font-semibold text-emerald-950 mb-4">Saved Cards</h3>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No saved cards</p>
                <p className="text-sm text-stone-400 mt-1">
                  Add a card for faster checkout
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      method.is_default
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{getCardIcon(method.brand)}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-emerald-950 capitalize">
                            {method.brand}
                          </span>
                          <span className="text-stone-500">•••• {method.last4}</span>
                          {method.is_default && (
                            <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">
                          Expires {formatExpiry(method.exp_month, method.exp_year)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!method.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(method.id)}
                          disabled={settingDefaultId === method.id}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          {settingDefaultId === method.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCard(method.id)}
                        disabled={deletingId === method.id}
                        className="text-red-500 hover:text-red-600"
                      >
                        {deletingId === method.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Security Info */}
          <div className="mt-8 p-4 bg-stone-100 rounded-xl">
            <h4 className="font-medium text-emerald-950 mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Your Security
            </h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Card details are encrypted and stored by Stripe</li>
              <li>• We only store a secure token reference, not your card number</li>
              <li>• All transactions are protected by 3D Secure when available</li>
              <li>• You can remove your cards at any time</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentMethodsPage;
